import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";

import { makeStyles } from "tss-react/mui";

import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";

import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import NorthIcon from "@mui/icons-material/North";
import SouthIcon from "@mui/icons-material/South";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import Paper from "@mui/material/Paper";

import DqmClass from "../../../service/dqm";
import AlertWhenPanel from "./AlertWhenPanel";
import { ButtonUI } from "../../Common";
import AlertRecipientsPanel from "./AlertRecipientsPanel";
import { navDataExplorer } from "../../kmk/AppData";
import KMKTable from "../../Common/Table/KMKTable";
import MultiSelectDropDown from "../../Common/MultiSelectDropDown";
import MultiSelect from "../../../util/multiselect";
import { CATEGORIES, CRITICALITY, DASHBOARD_COLUMNS } from "../DQMData";

const useStyles = makeStyles()((theme) => ({
  tableHeader: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  searchContainer: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  progressContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    color: "#666666",
    fontSize: "12px",
  },
  LinearProgress: {
    height: 5,
    borderRadius: 5,
    width: "80%",
  },
  findings: {
    display: "flex",
    alignItems: "center",
    justifyContent: "end",

    "& > .MuiSvgIcon-root": {
      minWidth: "40px",
    },

    "& .MuiButtonBase-root": {
      minWidth: "fit-content",
      width: "fit-content",
      padding: 0,
      margin: 0,
    },
  },
  ruleContainer: {
    display: "flex",
    alignItems: "center",

    "& > .MuiSvgIcon-root": {
      display: "inline-block",
      marginRight: "3px",
    },

    "& > span": {
      display: "inline-block",
      lineHeight: "100%",
    },

    margin: "4px 0 4px 0",
  },
  capitalizeWords: {
    textTransform: "capitalize",
  },
  DataTableContainer: {
    borderRadius: 10,
    backgroundColor: "#FFFFFF",
    padding: "5px 0px 0px",
    overflowX: "auto",
    "& .MuiTableRow-head .MuiTableCell-root": {
      padding: "6px 16px",
      height: "37px",
    },
    "& .MuiTableRow-head .MuiTableCell-root:last-child": {
      paddingRight: "40px",
    },
    "& .MuiTableBody-root .MuiTableCell-root": {
      padding: "6px 16px",
      borderBottom: "none",
    },
    "& .MuiTableBody-root .MuiTableCell-root>.MuiIconButton-root:first-child": {
      marginLeft: "-10px",
      verticalAlign: "sub",
    },
    "& ::-webkit-scrollbar-track": {
      marginTop: "36px",
    },
  },
}));

const FieldsTable = forwardRef((props, ref) => {
  const { Workspace_id, datasource, accessRole, getFindingsCounts, loadingFields, setLoadingFields } = props;

  const { classes } = useStyles();
  const [search, setSearch] = useState("");
  const [fields, setFields] = useState([]);
  const [alertWhenOpen, setAlertWhenOpen] = useState(false);
  const [alertRecipientsOpen, setAlertRecipientsOpen] = useState(false);
  const [alertField, setAlertField] = useState({});

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("Attribute");
  const [selectedCategory, setSelectedCategory] = useState([]);
  const [selectedCriticality, setSelectedCriticality] = useState([]);

  //Filtering fields based on search or field change
  const filteredFields = React.useMemo(() => {
    const searchTerm = search.trim().toLowerCase();

    return fields.filter(({ Attribute, AlertWhen, RuleName = "", CategoryName = "", Alert = "" }) => {
      // Category filter
      if (!selectedCategory.includes(CategoryName)) return false;
      // Criticality filter
      if (!selectedCriticality.includes(Alert)) return false;

      const normalizedAlertWhen = (AlertWhen ?? "").toLowerCase();
      const normalizedAttribute = Attribute.toLowerCase();

      const normalizedRule = (RuleName || "").toLowerCase();

      return (
        normalizedRule.includes(searchTerm) ||
        normalizedAttribute.includes(searchTerm) ||
        normalizedAlertWhen.includes(searchTerm)
      );
    });
  }, [fields, search, selectedCategory, selectedCriticality]);

  const toggleAlert = useCallback(() => setAlertWhenOpen((prev) => !prev), []);
  const toggleAlertRecipients = useCallback(() => setAlertRecipientsOpen((prev) => !prev), []);
  const setAlertWhenField = useCallback((field) => setAlertField(field), []);

  //Navigating to sql explorer with query
  const navigateToExplorer = React.useCallback(
    (DQMRule_id) => {
      const url = `./${navDataExplorer.link}`;
      const newTab = window.open(url, "_blank");

      const dqmClass = new DqmClass();
      dqmClass.getFindingsQuery(Workspace_id, DQMRule_id).then((response) => {
        newTab.data = response.data;
      });
    },
    [Workspace_id]
  );

  const $columns = useMemo(() => {
    const getRuleCell = (record) => (
      <Box className={classes.ruleContainer}>
        {record.Alert === "Critical" ? (
          <InfoOutlinedIcon color="error" style={iconStyle} />
        ) : (
          <WarningAmberRoundedIcon color="warning" style={iconStyle} />
        )}
        <Tooltip title={record.Expression} arrow placement="right">
          <span className={classes.capitalizeWords}>{record.RuleName}</span>
        </Tooltip>
      </Box>
    );

    const getHealthCell = (record) => (
      <Box className={classes.progressContainer}>
        {record.Health || 0}%
        <LinearProgress
          className={classes.LinearProgress}
          color="success"
          variant="determinate"
          value={record.Health}
          sx={{ backgroundColor: record.Alert === "Critical" ? "#EF5350" : "#FBC02D" }}
        />
      </Box>
    );

    const getFindingsCell = (record) => (
      <span className={classes.findings}>
        {record.Findings ? (
          <Button color="info" variant="text" onClick={() => navigateToExplorer(record.DQMRule_id)}>
            {record.Findings}
          </Button>
        ) : (
          0
        )}
        {record.HealthDirection === 1 ? (
          <Tooltip title="Health deteriorated">
            <NorthIcon color="error" />
          </Tooltip>
        ) : record.HealthDirection === -1 ? (
          <Tooltip title="Health improved">
            <SouthIcon color="success" />
          </Tooltip>
        ) : (
          <span style={{ display: "inline-block", width: "40px" }} />
        )}
      </span>
    );

    const getAlertWhenCell = (record) => (
      <span className={classes.findings}>
        {(record.AlertWhen || "").toLowerCase().includes("health") ? record.AlertWhen + "%" : record.AlertWhen || ""}
        {accessRole && accessRole !== "Visitor" && (
          <IconButton
            aria-label="edit"
            onClick={() => {
              setAlertWhenField(record);
              toggleAlert();
            }}
            style={{ marginLeft: "8px" }}
          >
            {record.AlertWhen ? <EditOutlinedIcon color="info" /> : <AddCircleOutlineOutlinedIcon color="info" />}
          </IconButton>
        )}
      </span>
    );

    const iconStyle = { marginRight: "8px", height: "24px", width: "24px" };

    return (
      DASHBOARD_COLUMNS?.map((column) => ({
        id: column.id,
        name: column.label.toUpperCase(),
        style: { minWidth: 180, maxWidth: 290, wordBreak: "break-word" },
        align: column.align,
        sortable: column.sortable,
        renderHeadCell: (name) => <>{name}</>,
        renderValueCell: (val, record, index) => {
          switch (column.id) {
            case "RuleName":
              return getRuleCell(record);
            case "Health":
              return getHealthCell(record);
            case "Findings":
              return getFindingsCell(record);
            case "Alert_When":
              return getAlertWhenCell(record);
            default:
              return record?.[column.id] || "-";
          }
        },
      })) || []
    );
  }, [
    accessRole,
    classes.LinearProgress,
    classes.capitalizeWords,
    classes.findings,
    classes.progressContainer,
    classes.ruleContainer,
    navigateToExplorer,
    setAlertWhenField,
    toggleAlert,
  ]);

  const $sortedData = useMemo(() => {
    const primaryKey = orderBy;
    const sorted = [...filteredFields].sort((a, b) => {
      const isNumeric = ["Health", "Findings"].includes(primaryKey);

      const aPrimary = isNumeric ? parseInt(a[primaryKey]) || 0 : (a[primaryKey] || "").toLowerCase();
      const bPrimary = isNumeric ? parseInt(b[primaryKey]) || 0 : (b[primaryKey] || "").toLowerCase();

      const primaryResult = isNumeric
        ? order === "asc"
          ? aPrimary - bPrimary
          : bPrimary - aPrimary
        : order === "asc"
        ? aPrimary.localeCompare(bPrimary)
        : bPrimary.localeCompare(aPrimary);

      if (primaryResult !== 0) return primaryResult;

      // Secondary sort by Alert priority: CRITICAL > ACCEPTABLE
      const alertPriority = { critical: 2, acceptable: 1 };

      const aAlert = (a.Alert || "").toLowerCase();
      const bAlert = (b.Alert || "").toLowerCase();

      const aPriority = alertPriority[aAlert] || 0;
      const bPriority = alertPriority[bAlert] || 0;

      return bPriority - aPriority;
    });

    return sorted;
  }, [order, orderBy, filteredFields]);

  const handleSorting = useCallback((property) => {
    setOrderBy((prevProperty) => {
      setOrder((prevOrder) => {
        const isAsc = prevProperty === property && prevOrder === "asc";
        return isAsc ? "desc" : "asc";
      });

      return property;
    });
  }, []);

  const handleCateoryChange = useCallback((selectionList) => {
    setSelectedCategory(selectionList);
  }, []);

  const handleCriticality = useCallback((selectionList) => {
    setSelectedCriticality(selectionList);
  }, []);

  const handleClearSearch = useCallback(() => setSearch(""), []);

  const getFields = useCallback(
    (JobHistory_id) => {
      if (!datasource) return;

      setLoadingFields(true);

      const dqmClass = new DqmClass();
      dqmClass.getDqmFields(Workspace_id, datasource, JobHistory_id).then((response) => {
        if (response.success) setFields(response.data);

        setLoadingFields(false);
      });
    },
    [Workspace_id, datasource, setLoadingFields]
  );

  useImperativeHandle(ref, () => ({
    getFields,
    setFields,
  }));

  useEffect(() => {
    let criticalCount = 0;
    let warningCount = 0;

    fields.forEach((field) => {
      if (field.Alert === "Critical") criticalCount += Number(field.Findings);
      else if (field.Alert === "Acceptable") warningCount += Number(field.Findings);
    });

    getFindingsCounts({ criticalCount, warningCount }, fields.length);
  }, [fields, getFindingsCounts]);

  useEffect(() => {
    setSelectedCategory(CATEGORIES.map(({ value }) => value));
    setSelectedCriticality(CRITICALITY.map(({ value }) => value));
  }, []);

  //Save click of alert when panel
  const updateAlertWhen = (DQMRule_id, AlertWhen) => {
    setFields((prav) => prav.map((field) => (field.DQMRule_id === DQMRule_id ? { ...field, AlertWhen } : field)));
    toggleAlert();
  };

  return (
    <>
      <Box
        component="main"
        sx={{
          mt: 2,
          pb: 3,
        }}
      >
        <Box className={classes.tableHeader}>
          <Box className={classes.searchContainer}>
            <TextField
              margin="normal"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              variant="standard"
              InputProps={{
                startAdornment: <SearchIcon color="primary" />,
                ...(search && {
                  endAdornment: (
                    <IconButton onClick={handleClearSearch} size="small">
                      <ClearIcon color="primary" />
                    </IconButton>
                  ),
                }),
              }}
              sx={{ mr: "20px" }}
            />

            <Box>
              <MultiSelectDropDown
                dataid="dqmCriticality"
                variant="standard"
                placeHolder={<MultiSelect title="Criticality" dataLength={selectedCriticality.length} />}
                options={CRITICALITY}
                selected={selectedCriticality}
                isSelectAll={true}
                onSelectChange={(selectionList) => handleCriticality(selectionList)}
                disabled={loadingFields}
              />

              <MultiSelectDropDown
                dataid="dqmCategories"
                variant="standard"
                placeHolder={<MultiSelect title="Category" dataLength={selectedCategory.length} />}
                options={CATEGORIES}
                selected={selectedCategory}
                isSelectAll={true}
                onSelectChange={(selectionList) => handleCateoryChange(selectionList)}
                disabled={loadingFields}
              />
            </Box>
          </Box>

          {accessRole && accessRole !== "Visitor" && (
            <ButtonUI
              variant="contained"
              color="secondary"
              style={{ minWidth: "150px", marginLeft: 15 }}
              onClick={toggleAlertRecipients}
            >
              Configure recipients
            </ButtonUI>
          )}
        </Box>

        <Paper sx={{ width: "100%", overflow: "hidden" }} className={classes.DataTableContainer}>
          <KMKTable
            dataIdKey="DQM_RULE_TABLE"
            loading={loadingFields}
            columns={$columns}
            data={$sortedData}
            sortingProperties={{ order, orderBy, handleSorting }}
            paginationProperties={{}}
            tableProps={{ size: "small", stickyHeader: true }}
            boldHeader
            containerHeight={500}
            noDataText={datasource ? "No records found" : "No dataset selected"}
          />
        </Paper>
      </Box>
      {alertWhenOpen && (
        <AlertWhenPanel
          Workspace_id={Workspace_id}
          alertField={alertField}
          toggleAlert={toggleAlert}
          updateAlertWhen={updateAlertWhen}
        />
      )}

      {alertRecipientsOpen && (
        <AlertRecipientsPanel Workspace_id={Workspace_id} toggleAlertRecipients={toggleAlertRecipients} />
      )}
    </>
  );
});

export default FieldsTable;
